// ============================================================
// TaskFlow OS - Supabase Client
// ============================================================

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Supabase credentials not found. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env'
  );
}

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
  }
);

// ============================================================
// Auth Helpers
// ============================================================

export async function signUp(email: string, password: string, displayName: string) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        display_name: displayName,
      },
    },
  });
  return { data, error };
}

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  return { error };
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

// ============================================================
// Event Logger
// ============================================================

export async function logEvent(
  eventType: string,
  payload: Record<string, unknown> = {},
  options: {
    projectId?: string;
    workflowId?: string;
    nodeId?: string;
    nodeType?: string;
  } = {}
) {
  const user = await getCurrentUser();
  if (!user) return;

  const { error } = await supabase.from('event_log').insert({
    event_type: eventType,
    actor_id: user.id,
    project_id: options.projectId,
    workflow_id: options.workflowId,
    node_id: options.nodeId,
    node_type: options.nodeType,
    payload,
  });

  if (error) {
    console.error('Event logging failed:', error);
  }
}

// ============================================================
// Realtime Subscriptions
// ============================================================

export function subscribeToWorkflow(
  workflowId: string,
  callbacks: {
    onNodeChange?: (payload: unknown) => void;
    onEdgeChange?: (payload: unknown) => void;
  }
) {
  const channel = supabase.channel(`workflow:${workflowId}`);

  if (callbacks.onNodeChange) {
    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'operational_nodes',
        filter: `workflow_id=eq.${workflowId}`,
      },
      callbacks.onNodeChange
    );

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'approval_nodes',
        filter: `workflow_id=eq.${workflowId}`,
      },
      callbacks.onNodeChange
    );

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'design_nodes',
        filter: `workflow_id=eq.${workflowId}`,
      },
      callbacks.onNodeChange
    );
  }

  if (callbacks.onEdgeChange) {
    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'workflow_edges',
        filter: `workflow_id=eq.${workflowId}`,
      },
      callbacks.onEdgeChange
    );
  }

  channel.subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

export function subscribeToNotifications(
  userId: string,
  callback: (payload: unknown) => void
) {
  const channel = supabase
    .channel(`notifications:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      callback
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
